import { getCompanyInfo, getCompanyApiKey } from '../CompanyInfo/companies';

export const analyzeChatsWithAI = async (chatsData, userEmail = 'admin@purepaths.com') => {
  try {
    const companyInfo = getCompanyInfo(userEmail);
    const apiKey = getCompanyApiKey(userEmail);
    
    const defaultCompanyInfo = {
      name: "Pure Paths Admin",
      apiKey: process.env.REACT_APP_OPENAI_API_KEY,
      info: "Du er en AI-assistent som analyserer chat-data og lager rapporter for Pure Paths administrasjon."
    };

    const effectiveCompanyInfo = companyInfo || defaultCompanyInfo;
    const effectiveApiKey = apiKey || process.env.REACT_APP_OPENAI_API_KEY;

    console.log("Sending message to ChatGPT for company:", effectiveCompanyInfo.name);
    
    // Forbered en mer detaljert analyse av dataene
    const jsonData = JSON.parse(chatsData.content);
    const users = Object.entries(jsonData.users || {}).filter(([key]) => key !== 'PathwayGuide');
    
    // Samle all chat-data med sikker tidshåndtering
    const allChats = users.flatMap(([userId, userData]) => 
      (userData.chats || []).map(chat => {
        // Sikker konvertering av timestamp
        let timestamp;
        try {
          // Håndter både Unix timestamp og ISO string
          if (typeof chat.timestamp === 'number') {
            timestamp = new Date(chat.timestamp * 1000).toISOString();
          } else if (chat.ts) { // Håndter alternative timestamp formater
            timestamp = new Date(chat.ts * 1000).toISOString();
          } else {
            timestamp = new Date().toISOString(); // Fallback til nåværende tid
          }
        } catch (error) {
          console.warn('Invalid timestamp for chat:', chat);
          timestamp = new Date().toISOString();
        }

        return {
          ...chat,
          userId,
          timestamp
        };
      })
    );

    // Analyser sentiment og stress-indikatorer
    const sentimentAnalysis = allChats.reduce((acc, chat) => {
      const content = chat.content?.toLowerCase() || '';
      if (content.includes('bra') || content.includes('ja')) acc.positive++;
      else if (content.includes('nei') || content.includes('dårlig')) acc.negative++;
      else acc.neutral++;
      if (content.includes('stress') || content.includes('angst')) acc.stressIndicators++;
      return acc;
    }, { positive: 0, negative: 0, neutral: 0, stressIndicators: 0 });

    // Organiser meldinger etter uke
    const weeklyActivity = allChats.reduce((acc, chat) => {
      const week = chat.metadata?.weekNumber || 0;
      acc[week] = (acc[week] || 0) + 1;
      return acc;
    }, {});

    const analysisContent = {
      company: jsonData.company,
      exportDate: jsonData.export_date,
      userCount: users.length,
      totalMessages: allChats.length,
      sentimentAnalysis,
      weeklyActivity,
      stressIndicators: {
        count: sentimentAnalysis.stressIndicators,
        examples: allChats
          .filter(chat => 
            chat.content?.toLowerCase().includes('stress') || 
            chat.content?.toLowerCase().includes('angst'))
          .map(chat => chat.content)
      },
      positiveIndicators: {
        count: sentimentAnalysis.positive,
        examples: allChats
          .filter(chat => 
            chat.content?.toLowerCase().includes('bra') || 
            chat.content?.toLowerCase().includes('ja'))
          .map(chat => chat.content)
      },
      timeOfDayAnalysis: allChats.reduce((acc, chat) => {
        const hour = chat.metadata?.timeOfDay || 0;
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
      }, {}),
      dayOfWeekAnalysis: allChats.reduce((acc, chat) => {
        const day = chat.metadata?.dayOfWeek || 0;
        acc[day] = (acc[day] || 0) + 1;
        return acc;
      }, {})
    };
    
    const systemMessage = `Du er en profesjonell arbeidsplassanalytiker som spesialiserer seg på å analysere medarbeiderdata og lage detaljerte rapporter. 

Når du lager rapporten, følg denne strukturen:

1. Start med "Kjære [Firmanavn]"

2. Innledning
- Kort oppsummering av analysens omfang
- Tidsperiode for analysen
- Antall deltakere

3. Nøkkelfunn
- Generell stemning på arbeidsplassen (basert på sentiment-analyse)
- Stress- og trivselsnivåer
- Engasjementsnivå

4. Detaljert Analyse
- Kommunikasjonsmønstre
- Stress-indikatorer med konkrete eksempler
- Positive trender og høydepunkter
- Utfordringer som er identifisert

5. Spesifikke Observasjoner
- Sitater og eksempler fra meldinger (anonymisert)
- Mønstre i arbeidstid og aktivitet
- Tilbakemeldinger på arbeidsmengde

6. Anbefalinger
- Konkrete tiltak for forbedring
- Forslag til stressreduserende tiltak
- Anbefalte oppfølgingspunkter

7. Konklusjon
- Oppsummering av hovedfunn
- Prioriterte fokusområder
- Neste steg

Vær spesifikk, bruk eksempler fra dataene, og gi konkrete, handlingsorienterte anbefalinger. 
Fokuser spesielt på stress og trivsel, og hvordan disse kan forbedres.
Bruk en profesjonell, men omsorgsfull tone.`;

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${effectiveApiKey}`
      },
      body: JSON.stringify({
        model: "gpt-4",
        messages: [
          { role: "system", content: systemMessage },
          { 
            role: "user", 
            content: `Analyser følgende data og lag en detaljert rapport:\n${JSON.stringify(analysisContent, null, 2)}`
          }
        ],
        temperature: 0.7,
        max_tokens: 2000
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('OpenAI API Error:', errorData);
      throw new Error(`API request failed: ${response.status} - ${JSON.stringify(errorData)}`);
    }

    const responseData = await response.json();
    console.log('OpenAI Response:', responseData);

    return {
      detailedReport: {
        title: `Analyserapport for ${jsonData.company}`,
        formalReport: responseData.choices[0].message.content
      }
    };
    
  } catch (error) {
    console.error('Error in analyzeChatsWithAI:', error);
    throw new Error(`Analyse feilet: ${error.message}`);
  }
}; 